import { draftToFilterArrays, singleValueDraft } from "~/shared/lib/filter-utils";
import { parseDateRangeValue } from "~/shared/ui/data-table/DataTableFilter";

import type { Element, ElementSource } from "../types";

export const ELEMENT_SOURCES: readonly ElementSource[] = ["manual", "imported"];

export type ElementsFilters = {
    source: readonly string[];
    createdAt: readonly string[];
};

export const DEFAULT_ELEMENTS_FILTERS: ElementsFilters = {
    source: [],
    createdAt: [],
};

const ELEMENTS_FILTER_KEYS = Object.keys(DEFAULT_ELEMENTS_FILTERS) as (keyof ElementsFilters)[];

/** URL filters (arrays) → single-value draft for the filter panel. */
export function elementsDraftFromFilters(filters: ElementsFilters): Record<string, unknown> {
    return singleValueDraft(filters);
}

/** Single-value draft → URL filter arrays, written in ONE batched update (chats/assets precedent). */
export function applyElementsDraft(
    draft: Record<string, unknown>,
    setFilters: (entries: Partial<Record<keyof ElementsFilters, readonly string[]>>) => void,
): void {
    setFilters(draftToFilterArrays<ElementsFilters>(draft, ELEMENTS_FILTER_KEYS));
}

export type ElementSortField = "name" | "createdAt" | "imagesCount";

export interface ElementsQueryState {
    /** Free-text search over name + description (client-side — the backend has no search). */
    query: string;
    filters: ElementsFilters;
    sortBy: ElementSortField | undefined;
    desc: boolean;
    /** 1-based page. */
    page: number;
    pageSize: number;
}

export interface ElementsPageResult {
    items: Element[];
    /** Total AFTER filtering (drives pagination), not the raw library size. */
    total: number;
}

function matchesQuery(element: Element, needle: string): boolean {
    if (!needle) return true;
    const q = needle.trim().toLowerCase();
    if (!q) return true;
    return element.name.toLowerCase().includes(q) || element.description.toLowerCase().includes(q);
}

function matchesCreatedAt(element: Element, rangeValue: string | undefined): boolean {
    if (!rangeValue) return true;
    const { from, to } = parseDateRangeValue(rangeValue);
    if (!from && !to) return true;
    // `createdAt` is an ISO timestamp; the daterange filter stores plain `YYYY-MM-DD`
    // bounds, so compare on the date prefix (inclusive on both ends).
    const day = element.createdAt.slice(0, 10);
    if (from && day < from) return false;
    if (to && day > to) return false;
    return true;
}

function compare(a: Element, b: Element, sortBy: ElementSortField): number {
    if (sortBy === "name") return a.name.localeCompare(b.name);
    if (sortBy === "imagesCount") return a.images.length - b.images.length;
    return a.createdAt.localeCompare(b.createdAt);
}

/**
 * Search + filter + sort + paginate the whole library on the client.
 *
 * `GET /v2/elements` returns every element of the workspace in one response — there is
 * no server-side query surface — so the section view-kit contract (`items` + `total` for the
 * current page) is satisfied here instead of by the backend.
 */
export function selectElementsPage(
    all: readonly Element[],
    { query, filters, sortBy, desc, page, pageSize }: ElementsQueryState,
): ElementsPageResult {
    const source = filters.source[0];
    const createdAt = filters.createdAt[0];

    const filtered = all.filter(
        (element) =>
            matchesQuery(element, query) &&
            (!source || element.source === source) &&
            matchesCreatedAt(element, createdAt),
    );

    const sorted = sortBy
        ? [...filtered].sort((a, b) => (desc ? -compare(a, b, sortBy) : compare(a, b, sortBy)))
        : filtered;

    const offset = Math.max(0, (page - 1) * pageSize);
    return { items: sorted.slice(offset, offset + pageSize), total: sorted.length };
}

/** Table sort column id → the field the client sorter understands (unknown ids → undefined). */
export function elementSortField(sortId: string | undefined): ElementSortField | undefined {
    if (sortId === "name" || sortId === "createdAt" || sortId === "imagesCount") return sortId;
    return undefined;
}
